import { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  TextField,
  IconButton,
  List,
  ListItem,
  Avatar,
  CircularProgress,
  Divider,
  alpha,
  useTheme,
  Chip,
  Fade,
  Slide,
  useMediaQuery,
  keyframes
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import PersonIcon from '@mui/icons-material/Person';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import CloseIcon from '@mui/icons-material/Close';
import { useAgentClientChat } from '../contexts/AgentClientChatContext';
import { useClient } from '../contexts/ClientContext';
import { useAuth } from '../contexts/AuthContext';

// typing-dot animation
const typingDot = keyframes`
  0%, 80%, 100% { opacity: .2; }
  40%          { opacity: 1;  }
`;

const AgentClientChat = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();
  const { agent } = useClient();
  const { 
    messages,
    isLoading,
    isConnected,
    sendMessage,
    markAsRead
  } = useAgentClientChat();
  
  const [newMessage, setNewMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Mark messages as read when chat opens
  useEffect(() => {
    if (open) {
      const unreadMessages = messages.filter(msg => !msg.isRead && msg.senderType === 'agent');
      if (unreadMessages.length > 0) {
        const timer = setTimeout(() => {
          markAsRead();
        }, 1000);
        return () => clearTimeout(timer);
      }
    }
  }, [open, messages, markAsRead]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || isSending) return;

    setIsSending(true);
    try {
      const result = await sendMessage(newMessage.trim());
      if (result.success) {
        setNewMessage('');
      } else {
        console.error('Failed to send message:', result.error);
      }
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

      if (diffInHours < 24) {
        return date.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit'
        });
      } else {
        return date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
    } catch {
      return 'Unknown';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      sx={{
        // Set a very high z-index for the entire dialog
        zIndex: isMobile ? 4000 : 3500,
        '& .MuiDialog-paper': {
          zIndex: isMobile ? 4000 : 3500
        },
        '& .MuiBackdrop-root': {
          zIndex: isMobile ? 3999 : 3499
        }
      }}
      PaperProps={{
        sx: {
          height: isMobile ? '100dvh' : '80vh',        // ← use dynamic viewport
          maxHeight: isMobile ? '100dvh' : '600px',    // ← same here
          borderRadius: isMobile ? 0 : 2,
          position: isMobile ? 'fixed' : 'relative',
          top: isMobile ? 0 : 'auto',
          left: isMobile ? 0 : 'auto',
          width: isMobile ? '100vw' : 'auto',
          minWidth: isMobile ? 'auto' : '650px', // Add minimum width for desktop
          margin: isMobile ? 0 : 'auto',
          zIndex: isMobile ? 4000 : 3500
        }
      }}
    >
      {/* Chat Header */}
      <DialogTitle sx={{ 
        p: 2, 
        borderBottom: 1, 
        borderColor: 'divider',
        bgcolor: 'background.paper',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: isMobile ? 'sticky' : 'relative',
        top: 0,
        zIndex: isMobile ? 4001 : 3501
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar 
            src={agent?.picture}
            sx={{ 
              width: 40, 
              height: 40,
              bgcolor: 'primary.main'
            }}
          >
            <SupportAgentIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight="bold">
              {agent?.name || 'Your Agent'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: isConnected ? 'success.main' : 'grey.400'
              }} />
              <Typography variant="caption" color="text.secondary">
                {isConnected ? 'Online' : 'Offline'}
              </Typography>
              {agent?.businessName && (
                <Chip 
                  label={agent.businessName}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.65rem', height: 18 }}
                />
              )}
            </Box>
          </Box>
        </Box>
        
        {/* Remove the minimize button and just keep the close button */}
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Messages List */}
      <DialogContent sx={{ 
        p: 0, 
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: alpha(theme.palette.grey[100], 0.3)
      }}>
        <Box sx={{ 
          flex: 1, 
          overflowY: 'auto',
          overflowX: 'hidden',          // ← hide residual overflow
          p: 1
        }}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
              <CircularProgress size={40} />
              <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                Connecting to your agent...
              </Typography>
            </Box>
          ) : messages.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Avatar sx={{ 
                width: 60, 
                height: 60, 
                bgcolor: 'primary.main', 
                mx: 'auto', 
                mb: 2 
              }}>
                <SupportAgentIcon sx={{ fontSize: 30 }} />
              </Avatar>
              <Typography variant="h6" gutterBottom>
                Start chatting with {agent?.name || 'your agent'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Get personalized assistance with your property search and CribCritic analysis.
              </Typography>
            </Box>
          ) : (
            <List sx={{ py: 1 }}>
              {messages.map((message, index) => (
                message.isTyping ? (
                  <Fade key={message.id} in timeout={300}>
                    <ListItem
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: message.senderType === 'client' ? 'flex-end' : 'flex-start',
                        px: 2,
                        py: 0.5,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
                        <Avatar 
                          sx={{ width: 28, height: 28, bgcolor: 'action.hover' }}
                        >
                          {message.senderType === 'client' ? <PersonIcon fontSize="small"/> : <SupportAgentIcon fontSize="small"/>}
                        </Avatar>
                        <Box sx={{
                          p: 1,
                          px: 2,
                          borderRadius: 2,
                          bgcolor: alpha(theme.palette.text.primary, 0.08),
                          display: 'flex',
                          gap: 0.5
                        }}>
                          {[0,1,2].map(i => (
                            <Box key={i} sx={{
                              width: 6,
                              height: 6,
                              borderRadius: '50%',
                              bgcolor: 'text.secondary',
                              animation: `${typingDot} 1.4s infinite`,
                              animationDelay: `${i*0.2}s`
                            }} />
                          ))}
                        </Box>
                      </Box>
                    </ListItem>
                  </Fade>
                ) : (
                  <Fade
                    key={`${message.id}-${index}`}        // ← always unique
                    in
                    timeout={300}
                    style={{ transitionDelay: `${index * 50}ms` }}
                  >
                    <ListItem
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems:
                          message.senderType === 'client' ? 'flex-end' : 'flex-start',
                        px: 2,
                        py: 0.5,
                      }}
                    >
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'flex-end',
                        gap: 1,
                        overflowX: 'hidden'                 // ← prevent horizontal scroll
                      }}>
                        <Avatar 
                          src={message.senderType === 'client' ? user?.picture : agent?.picture}
                          sx={{ 
                            width: 28, 
                            height: 28,
                            bgcolor: message.senderType === 'client' ? 'primary.main' : 'secondary.main'
                          }}
                        >
                          {message.senderType === 'client' ? (
                            <PersonIcon fontSize="small" />
                          ) : (
                            <SupportAgentIcon fontSize="small" />
                          )}
                        </Avatar>
                        
                        <Slide 
                          direction={message.senderType === 'client' ? 'left' : 'right'} 
                          in 
                          timeout={300}
                        >
                          <Box sx={{ 
                            bgcolor: message.senderType === 'client' 
                              ? 'primary.main'
                              : 'background.paper',
                            color: message.senderType === 'client'
                              ? 'primary.contrastText'
                              : 'text.primary',
                            borderRadius: message.senderType === 'client'
                              ? '16px 16px 4px 16px'
                              : '16px 16px 16px 4px',
                            p: 1.5,
                            boxShadow: theme.shadows[1],
                            border: message.senderType === 'agent' 
                              ? `1px solid ${alpha(theme.palette.divider, 0.2)}`
                              : 'none',
                            position: 'relative',
                            maxWidth: { xs: '70%', sm: '75%', md: '80%' } // ← width cap on the bubble
                          }}>
                            <Typography variant="body2" sx={{ 
                              lineHeight: 1.4,
                              wordBreak: 'break-word',
                              fontSize: '0.875rem'
                            }}>
                              {message.message}
                            </Typography>
                            
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                opacity: 0.7,
                                fontSize: '0.65rem',
                                display: 'block',
                                mt: 0.5,
                                textAlign: message.senderType === 'client' ? 'right' : 'left'
                              }}
                            >
                              {formatTime(message.timestamp)}
                            </Typography>

                            {!message.isRead && message.senderType === 'agent' && (
                              <Box sx={{
                                position: 'absolute',
                                top: -3,
                                right: -3,
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                bgcolor: 'error.main',
                                border: '1px solid white'
                              }} />
                            )}
                          </Box>
                        </Slide>
                      </Box>
                    </ListItem>
                  </Fade>
                )
              ))}
            </List>
          )}
          <div ref={messagesEndRef} />
        </Box>

        <Divider />

        {/* Message Input */}
        <Box sx={{ 
          p: 2,
          pb: isMobile ? 'calc(env(safe-area-inset-bottom) + 8px)' : 2, // ← extra bottom padding
          bgcolor: 'background.paper',
          position: isMobile ? 'sticky' : 'relative',
          bottom: 0,
          zIndex: isMobile ? 4000 : 3500
        }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
            <TextField
              fullWidth
              multiline
              maxRows={3}
              placeholder={`Message ${agent?.name || 'your agent'}...`}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isSending || !isConnected}
              variant="outlined"
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  '&:hover': {
                    '& > fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                  '&.Mui-focused': {
                    boxShadow: theme => `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
                  },
                },
              }}
            />
            <IconButton
              color="primary"
              onClick={handleSendMessage}
              disabled={!newMessage.trim() || isSending || !isConnected}
              sx={{
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                '&:hover': {
                  bgcolor: 'primary.dark',
                },
                '&:disabled': {
                  bgcolor: 'grey.300',
                  color: 'grey.500',
                },
                width: 40,
                height: 40
              }}
            >
              {isSending ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <SendIcon fontSize="small" />
              )}
            </IconButton>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AgentClientChat;